import React from 'react';
import { getReadinessLabel } from './utils/readinessScore';

const BREAKDOWN_LABELS = {
  controls:    'Controls Implemented',
  evidence:    'Evidence Coverage',
  tasks:       'POA&M On-Track',
  narratives:  'Narratives Documented',
  affirmation: 'Annual Affirmation',
};

export default function ReadinessGauge({ result, size = 160 }) {
  if (!result) return null;

  const { score, breakdown } = result;
  const { label, color }     = getReadinessLabel(score);

  const cx     = size / 2;
  const cy     = size / 2;
  const radius = size / 2 - 16;
  const circ   = 2 * Math.PI * radius;
  const filled = (score / 100) * circ;
  const offset = circ - filled;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 }}>
      {/* Circular gauge */}
      <div style={{ position: 'relative', width: size, height: size }}>
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
          {/* Track */}
          <circle
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth={10}
          />
          {/* Progress */}
          <circle
            cx={cx} cy={cy} r={radius}
            fill="none"
            stroke={color}
            strokeWidth={10}
            strokeLinecap="round"
            strokeDasharray={circ}
            strokeDashoffset={offset}
            style={{ transition: 'stroke-dashoffset 0.8s ease, stroke 0.4s ease' }}
          />
        </svg>
        {/* Center text */}
        <div style={{
          position: 'absolute', inset: 0,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontSize: size * 0.22, fontWeight: 700, color, lineHeight: 1 }}>
            {score}%
          </span>
          <span style={{ fontSize: size * 0.1, color: 'var(--text3)', marginTop: 2, textAlign: 'center', maxWidth: size * 0.6 }}>
            {label}
          </span>
        </div>
      </div>

      {/* Breakdown rows */}
      <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 8 }}>
        {Object.entries(breakdown).map(([key, { score: s, weight }]) => (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: 'var(--text3)', flex: 1 }}>
              {BREAKDOWN_LABELS[key]}
              <span style={{ marginLeft: 4, opacity: 0.6 }}>({weight}%)</span>
            </span>
            <div style={{
              width: 80, height: 5, borderRadius: 3,
              background: 'var(--border)', overflow: 'hidden',
            }}>
              <div style={{
                height: '100%', borderRadius: 3,
                width: `${s}%`,
                background: s >= 75 ? 'var(--green)' : s >= 50 ? '#f59e0b' : 'var(--red)',
                transition: 'width 0.6s ease',
              }} />
            </div>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--text)', width: 30, textAlign: 'right' }}>
              {s}%
            </span>
          </div>
        ))}
      </div>

      {/* C3PAO CTA */}
      {score >= 85 && (
        <a
          href="https://cyberab.org/marketplace"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', borderRadius: 8,
            background: 'var(--green-bg)', border: '1px solid var(--green)',
            color: 'var(--green)', fontSize: 12, fontWeight: 600,
            textDecoration: 'none', cursor: 'pointer',
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
            <polyline points="22 4 12 14.01 9 11.01"/>
          </svg>
          Find a C3PAO Assessor
        </a>
      )}
    </div>
  );
}
